import React, { useContext, useEffect, useRef, useState } from 'react';

import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import RefreshIcon from '@mui/icons-material/Refresh';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import LinkIcon from '@mui/icons-material/Link';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { type ProviderContext, useSnackbar } from 'notistack';

import { downloadLensCsv } from '~/utils/downloadLensCsv';

import Reading from './Reading';
import Breakdown from './blocks/Breakdown';
import Calendar from './blocks/Calendar';
import Bridge from './blocks/Bridge';
import Bullet from './blocks/Bullet';
import BoxPlot from './blocks/BoxPlot';
import { TextScaleContext, textScaleOf } from './blocks/Chart';
import CategoryLine, { categoryLegend } from './blocks/CategoryLine';
import Combo, { comboLegend } from './blocks/Combo';
import DetailTable from './blocks/DetailTable';
import Figures from './blocks/Figures';
import Funnel from './blocks/Funnel';
import ParamControls from './ParamControls';
import Heatmap from './blocks/Heatmap';
import Legend, { type LegendItem } from './blocks/Legend';
import Prose from './blocks/Prose';
import Scatter from './blocks/Scatter';
import Share from './blocks/Share';
import PivotTable, { acrossColumn } from './blocks/PivotTable';
import SplitBreakdown from './blocks/SplitBreakdown';
import Trend, { trendLegend } from './blocks/Trend';
import ViewFrame from './blocks/ViewFrame';
import AddBlock from './AddBlock';
import BlockMenu, { type BlockMenuItem } from './BlockMenu';
import ReaskBlockMenu from './ReaskBlockMenu';
import PickPopover, { SEARCH_FROM } from './PickPopover';
import InlineText from './InlineText';
import { blockPng } from './blockImage';
import ResizeHandle from './ResizeHandle';
import { type Direction, moveOperation } from './editor';
import { TREND_FORMS, splitBlockId } from './localOperations';
import { type Shown, humanise, shownFor } from './format';
import type { Palette } from './palette';
import { switchesInTitles } from './lensState';
import { SeriesFocusContext, useSeriesFocus } from './seriesFocus';
import type { BlockPaneView } from './BlockPane';
import { paramOptionLabel } from './titles';
import { type Block, type BlockFamily, type Envelope, type LensDocument, type LensDocumentData, type LensState, type PlannedBlock, type SqlParam, type Tabs, blockKind } from './types';
import { type BoardEditor, fades } from './useBoardEdit';
import useElementWidth from './useElementWidth';

export type BoardContext = {
  document: LensDocument;
  data?: LensDocumentData;
  state: LensState;
  palette?: Palette;
  labels: Map<string, string>;
  setParam?: (name: string, value: string | undefined) => void;
  setPeriod?: (period: string) => void;
  pick?: (column: string, member: string) => void;
  editor?: BoardEditor;
  askAbout?: (block: { id: string; title: string }, draft?: string) => void;
  inspect?: (block: { id: string; title: string }, view: BlockPaneView) => void;
  follow?: (block: { id: string; title: string }) => void;
  editBlock?: (blockId: string) => void;
  asked?: string;
  landed?: Set<string>;
  failed?: boolean;
  fading?: Array<string>;
  sharedLegend?: Set<string>;
  selected?: string;
  select?: (blockId: string | undefined) => void;
  dragStart?: (blockId: string, title: string, event: React.PointerEvent) => void;
  dragging?: string;
  tabs?: { chosen: Array<string>; choose: (group: Tabs, tabId: string) => void; every?: boolean; narrow: boolean };
};

const ARROWS: Record<string, Direction> = { ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right' };

function TitleText({ text, context }: { text: string; context: BoardContext }) {
  const parts = text.replace(/\s*·\s*(?=\{\$)/g, ' ').split(/(\{\$[a-z0-9_]+\})/i);
  return <>{parts.map((part, index) => (/^\{\$/.test(part) ? <ParamToken context={context} key={index} name={part.slice(2, -1)} /> : part))}</>;
}

function ParamToken({ name, context }: { name: string; context: BoardContext }) {
  const param = context.document.params[name];
  const current = String(context.data?.params[name] ?? '');
  const options = param && 'measures' in param ? param.measures : param && 'columns' in param ? param.columns : param && 'sql' in param ? param.options : [];
  const label = (option: string) => paramOptionLabel(param, context.labels, option);
  if (!context.setParam || options.length < 2) return <>{label(current)}</>;
  return (
    <span className="tok">
      {label(current)}
      {options.length >= SEARCH_FROM ? <PickPopover clearable={false} label={param && 'label' in param && param.label ? param.label : humanise(name)} onPick={(next) => context.setParam!(name, next[0])} options={options.map((value) => ({ value, label: label(value) }))} overlay picked={[current]} single>{null}</PickPopover> : <select aria-label={param && 'label' in param && param.label ? param.label : humanise(name)} onChange={(event) => context.setParam!(name, event.target.value)} value={current}>
        {options.map((option) => (
          <option key={option} label={label(option)} value={option} />
        ))}
      </select>}
    </span>
  );
}

function valueToken(value: unknown, context: BoardContext) {
  const text = String(value ?? '');
  if (text.startsWith('$')) return <ParamToken context={context} name={text.slice(1)} />;
  return <>{context.labels.get(text) ?? humanise(text)}</>;
}

function Title({ block, kind, family, envelope, context }: { block: Block; kind: string; family?: BlockFamily; envelope?: Envelope; context: BoardContext }) {
  if (block.title) return <TitleText context={context} text={String(block.title)} />;
  const by = block.by?.[0];
  const byLabel = by && !by.startsWith('$') ? envelope?.columns.find((column) => column.name === by)?.label ?? humanise(by) : undefined;
  const split = by?.startsWith('$') ? <> {valueToken(by, context)}</> : byLabel ? <> by {byLabel.toLowerCase()}</> : null;
  if (family === 'table') return <>Detail</>;
  if (family === 'bridge') return <>{valueToken(block[kind], context)} change</>;
  if (family === 'time' || family === 'breakdown')
    return (
      <>
        {valueToken(block[kind], context)}
        {split}
      </>
    );
  return null;
}

type TextPart = string;

type TextEdit = { picked?: string; commit: (part: TextPart, value: string) => void; cancel: () => void };

function Header({ block, kind, planned, envelope, context, legend, text }: { block: Block; kind: string; planned?: PlannedBlock; envelope?: Envelope; context: BoardContext; legend?: React.ReactNode; text?: TextEdit }) {
  const title = <Title block={block} context={context} envelope={envelope} family={planned?.family} kind={kind} />;
  const about = !context.editor && context.inspect ? (name: string) => context.inspect!({ id: block.id, title: name || planned?.name || block.id }, 'about') : undefined;
  const subtitle = block.subtitle === false ? undefined : typeof block.subtitle === 'string' ? block.subtitle : planned?.subtitle;
  if (!block.title && !subtitle && !text && !['time', 'breakdown', 'bridge', 'table'].includes(planned?.family ?? '')) return null;
  return (
    <div className="bh" data-align={block.titleAlign === 'center' ? 'center' : undefined} data-title-size={typeof block.titleSize === 'number' ? 'px' : typeof block.titleSize === 'string' ? block.titleSize : undefined} style={typeof block.titleSize === 'number' ? ({ '--title-px': `${block.titleSize}px` } as React.CSSProperties) : undefined}>
      <h3
        data-lens-part="@title"
        onClick={about ? (event) => { if (!(event.target as HTMLElement).closest('.tok, select, button')) about(event.currentTarget.textContent ?? ''); } : undefined}
        style={about ? { cursor: 'pointer' } : undefined}
      >
        {text?.picked === '@title' ? (
          <InlineText label="Block title" onCancel={text.cancel} onCommit={(value) => text.commit('title', value)} placeholder={planned?.name} value={typeof block.title === 'string' ? block.title : ''} />
        ) : (
          title
        )}
      </h3>
      {text?.picked === '@subtitle' ? (
        <InlineText label="Subtitle" onCancel={text.cancel} onCommit={(value) => text.commit('subtitle', value)} value={typeof block.subtitle === 'string' ? block.subtitle : planned?.subtitle ?? ''} />
      ) : subtitle ? (
        <span className="sub" data-lens-part="@subtitle">
          {subtitle}
        </span>
      ) : null}
      {about && title ? (
        <button aria-label={`About ${planned?.name ?? 'this block'}`} className="info" onClick={(event) => about(event.currentTarget.parentElement?.querySelector('h3')?.textContent ?? '')} title="About this block" type="button">
          <InfoOutlinedIcon fontSize="inherit" />
        </button>
      ) : null}
      {legend}
    </div>
  );
}

function shownKind(block: Block, planned: PlannedBlock | undefined) {
  const own = blockKind(block);
  return planned?.kind && own && TREND_FORMS.includes(planned.kind) && TREND_FORMS.includes(own) ? own : (planned?.kind ?? own);
}

export function blockLegend(block: Block, context: BoardContext, width: number): Array<LegendItem> {
  const planned = context.data?.blocks[block.id];
  const envelope = context.data?.queries[planned?.queries?.[0] ?? planned?.query ?? ''];
  if (!planned || !envelope || envelope.status === 'error' || !envelope.rows.length || !context.palette) return [];
  const kind = shownKind(block, planned) as 'line' | 'area' | 'column';
  if (planned.view === 'combo') return comboLegend(envelope, block, context.palette);
  if (planned.view !== 'trend') return [];
  const timeless = envelope.columns.length > 0 && !envelope.columns.some((column) => column.role === 'time');
  return (timeless ? categoryLegend(envelope, context.palette, kind, block) : undefined) ?? trendLegend({ block, kind, envelope, palette: context.palette, width }) ?? [];
}

export function controlReach({ document, state }: Pick<BoardContext, 'document' | 'state'>, envelopes: Array<Envelope>): { reached: Array<string>; unreached: Array<string> } {
  if (!envelopes.length) return { reached: [], unreached: [] };
  const inTitles = switchesInTitles(document);
  const set = [
    ...document.filters.filter((filter) => (state.filters?.[filter.column] ?? filter.value)?.length).map((filter) => ({ key: filter.column, label: filter.label ?? humanise(filter.column) })),
    ...Object.entries(document.params)
      .filter((entry): entry is [string, SqlParam] => 'sql' in entry[1] && !entry[1].role && !inTitles.has(entry[0]) && state.params?.[entry[0]] !== undefined)
      .map(([name, param]) => ({ key: `$${name}`, label: param.label ?? humanise(name) })),
  ];
  const reaches = (control: { key: string }) => envelopes.some((envelope) => envelope.reach?.includes(control.key));
  return { reached: set.filter(reaches).map((control) => control.label), unreached: set.filter((control) => !reaches(control)).map((control) => control.label) };
}

type ViewProps = { block: Block; kind: string; envelope: Envelope; envelopes: Array<Envelope>; context: BoardContext; width: number; shown: Shown; picked?: string };

export const LINKED_BLOCK_PARAM = 'block';

function pickColumn(block: Block, context: BoardContext): string | undefined {
  const by = block.by?.[0];
  if (!by?.startsWith('$')) return by;
  const resolved = context.data?.params[by.slice(1)];
  return typeof resolved === 'string' && resolved !== 'none' ? resolved : undefined;
}

function pickerFor(block: Block, context: BoardContext): ((member: string) => void) | undefined {
  const by = pickColumn(block, context);
  if (!by || !context.pick || context.editor || (block.select !== undefined && block.select !== true)) return undefined;
  return (member) => context.pick!(by, member);
}

const VIEWS: Record<string, (props: ViewProps) => React.ReactNode> = {
  figures: ({ block, envelopes, context, width, shown, picked }) => {
    const selectParam = typeof block.select === 'string' ? block.select : undefined;
    return (
      <Figures
        block={block}
        envelopes={envelopes}
        lifted={context.dragging?.startsWith(`${block.id}/`) ? context.dragging.slice(block.id.length + 1) : undefined}
        onSelect={selectParam && context.setParam ? (measure) => context.setParam!(selectParam, measure) : undefined}
        picked={picked}
        selected={selectParam ? String(context.data?.params[selectParam] ?? '') : undefined}
        shown={shown}
        width={width || 1200}
      />
    );
  },
  trend: ({ block, kind, envelope, context, width }) =>
    context.palette && context.data ? (
      envelope.columns.length && !envelope.columns.some((column) => column.role === 'time') ? (
        <CategoryLine block={block} envelope={envelope} kind={kind as 'line' | 'area' | 'column'} lensWindow={context.data.period.current} palette={context.palette} width={width} />
      ) : (
        <Trend block={block} envelope={envelope} kind={kind as 'line' | 'area' | 'column'} lensWindow={context.data.period.current} onPeriod={context.setPeriod} palette={context.palette} width={width} />
      )
    ) : null,
  breakdown: ({ block, envelope, context, shown }) => {
    if (envelope.columns.filter((column) => column.role === 'measure').length > 1) return <DetailTable block={block} envelope={envelope} shown={shown} />;
    if (envelope.columns.filter((column) => column.role === 'dim').length > 1) return context.palette ? <SplitBreakdown envelope={envelope} palette={context.palette} stacked={block.stack === true} /> : null;
    const by = pickColumn(block, context);
    const pick = pickerFor(block, context);
    return <Breakdown block={block} envelope={envelope} onPick={pick} palette={context.palette} selected={by ? context.state.select?.[by] ?? [] : []} shown={shown} />;
  },
  bridge: ({ block, envelope, context }) => (context.palette ? <Bridge block={block} envelope={envelope} palette={context.palette} /> : null),
  table: ({ block, envelope, context, shown, picked }) => typeof block.across === 'string' && acrossColumn(envelope, block.across) ? <PivotTable block={block} across={acrossColumn(envelope, block.across)!} envelope={envelope} shown={shown} totalLabel={typeof block.totalLabel === 'string' ? block.totalLabel : undefined} /> : <DetailTable block={block} envelope={envelope} onPick={pickerFor(block, context)} picked={picked} selected={context.state.select?.[pickColumn(block, context) ?? ''] ?? []} shown={shown} totalLabel={typeof block.totalLabel === 'string' ? block.totalLabel : typeof block.top === 'number' && block.other === false ? `Top ${block.top}` : undefined} />,
  pie: ({ block, envelope, context, shown }) => {
    const by = pickColumn(block, context);
    const pick = pickerFor(block, context);
    return context.palette ? <Share block={block} envelope={envelope} onPick={pick} palette={context.palette} selected={by ? context.state.select?.[by] ?? [] : []} shown={shown} /> : null;
  },
  box: ({ block, envelope, context }) => (context.palette ? <BoxPlot block={block} envelope={envelope} onPick={pickerFor(block, context)} palette={context.palette} selected={context.state.select?.[pickColumn(block, context) ?? ''] ?? []} /> : null),
  combo: ({ block, envelope, context }) => (context.palette ? <Combo block={block} envelope={envelope} palette={context.palette} /> : null),
  funnel: ({ envelope, context }) => (context.palette ? <Funnel envelope={envelope} palette={context.palette} /> : null),
  calendar: ({ block, envelope, context, width }) => context.palette ? <Calendar block={block} envelope={envelope} onPeriod={context.setPeriod} palette={context.palette} width={width} /> : null,
  heatmap: ({ envelope }) => <Heatmap envelope={envelope} />,
  scatter: ({ block, envelope, context }) => (context.palette ? <Scatter block={block} envelope={envelope} palette={context.palette} /> : null),
  bullet: ({ block, envelope, shown }) => <Bullet block={block} envelope={envelope} shown={shown} />,
  code: ({ block, envelopes, context, width, shown }) => (
    <ViewFrame
      block={block}
      envelopes={envelopes}
      measures={context.document.measures}
      palette={context.palette}
      pick={context.pick}
      setParam={context.setParam}
      shown={shown}
      state={context.state}
      view={context.document.views[String(block.view)]}
      width={width}
    />
  ),
};

export function hiddenInView(block: Block, context: BoardContext): boolean {
  if (context.editor) return false;
  const kind = blockKind(block);
  const when = block.when as Record<string, Array<string>> | undefined;
  if (when && !Object.entries(when).every(([key, values]) => {
    const name = key.replace(/^\$/, '');
    const param = context.document.params[name];
    const fallback = param && ('measures' in param ? param.measures[0] : 'columns' in param ? param.columns[0] : 'sql' in param ? param.options[0] : param.value);
    return values.includes(String(context.state.params?.[name] ?? context.data?.params[name] ?? fallback));
  })) return true;
  return kind === 'pending' || Boolean(kind && context.data?.blocks[block.id]?.view === 'prose' && !String(block[kind] ?? '').trim());
}

class BlockBoundary extends React.Component<{ children: React.ReactNode; data: Array<Envelope | undefined> }, { failed?: string; data?: Array<Envelope | undefined> }> {
  state: { failed?: string; data?: Array<Envelope | undefined> } = {};

  static getDerivedStateFromProps(props: { data: Array<Envelope | undefined> }, state: { data?: Array<Envelope | undefined> }) {
    const arrived = props.data.length !== state.data?.length || props.data.some((entry, index) => entry !== state.data![index]);
    return arrived ? { data: props.data, failed: undefined } : null;
  }

  static getDerivedStateFromError(error: Error) {
    return { failed: error.message };
  }

  componentDidCatch(error: Error) {
    console.error('Lens block could not be drawn', error);
  }

  render() {
    return this.state.failed ? <div className="fail" role="alert">This card could not be drawn.</div> : this.props.children;
  }
}

export default function BlockView({ block, context }: { block: Block; context: BoardContext }) {
  const Menu: React.ComponentType<React.ComponentProps<typeof ReaskBlockMenu>> = context.editor && block.ask ? ReaskBlockMenu : BlockMenu;
  const planned = context.data?.blocks[block.id];
  const kind = shownKind(block, planned);
  const frame = useRef<HTMLDivElement>(null);
  const held = useRef<Array<Envelope | undefined>>([]);
  const width = useElementWidth(frame);
  const editor = context.editor;
  const snackbar: ProviderContext | undefined = useSnackbar();
  const blockTitle = () => frame.current?.querySelector('h3')?.textContent || planned?.name || block.id;
  const lensId = context.data?.lensId;
  const downloadable = Boolean(lensId && (planned?.query || planned?.queries?.length));
  const download = () =>
    lensId &&
    downloadLensCsv(lensId, { state: context.state, blocks: [block.id] }).catch(() =>
      snackbar?.enqueueSnackbar(`${blockTitle()} didn’t download. Try again.`, { variant: 'warning' }),
    );
  const image = async () => {
    const link = document.createElement('a');
    link.href = await blockPng(frame.current!, context.palette?.surface ?? '#fff', '.ask, .resize, .resize-bottom');
    link.download = `${blockTitle().replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase() || block.id}.png`;
    link.click();
  };
  const picked = editor && context.selected?.startsWith(`${block.id}/`) ? context.selected.slice(block.id.length + 1) : undefined;
  const selected = Boolean(editor) && (context.selected === block.id || picked !== undefined);
  const partHit = useRef(false);
  const rowFocus = useContext(SeriesFocusContext);
  const ownFocus = useSeriesFocus();
  const { select } = context;
  useEffect(() => {
    const node = frame.current;
    if (!node || !selected || !select) return undefined;
    const onPart = (event: Event) => {
      partHit.current = true;
      select(`${block.id}/${(event as CustomEvent<string>).detail}`);
    };
    node.addEventListener('lens-part', onPart);
    return () => node.removeEventListener('lens-part', onPart);
  }, [selected, select, block.id]);
  useEffect(() => {
    frame.current?.querySelectorAll<HTMLElement>('[data-lens-part]').forEach((node) => node.classList.toggle('picked', node.dataset.lensPart === picked));
  });
  const text: TextEdit | undefined = editor
    ? {
        picked,
        commit: (part, value) => {
          const before = block[part];
          if (value !== (typeof before === 'string' ? before : '')) editor.apply({ op: 'set', id: block.id, patch: { [part]: value || null } });
          context.select?.(block.id);
        },
        cancel: () => context.select?.(block.id),
      }
    : undefined;
  useEffect(() => {
    if (editor?.moved?.id === block.id) frame.current?.focus({ preventScroll: true });
  }, [editor?.moved, block.id]);
  const editing = editor
    ? {
        tabIndex: 0,
        onPointerDown: (event: React.PointerEvent) => {
          const target = event.target as HTMLElement;
          if (target.closest('.ask, .resize, .resize-bottom, .MuiMenu-root')) return;
          const tile = Array.isArray(block.kpis) && block.kpis.length > 1 ? target.closest<HTMLElement>('[data-lens-item]') : null;
          if (tile) context.dragStart?.(`${block.id}/${tile.dataset.lensItem}`, tile.querySelector('.l')?.textContent ?? tile.dataset.lensItem!, event);
          else context.dragStart?.(block.id, blockTitle(), event);
        },
        'aria-selected': selected,
        onClick: (event: React.MouseEvent) => {
          const target = event.target as HTMLElement;
          if (!event.currentTarget.contains(target) || target.closest('.ask')) return;
          if (partHit.current) {
            partHit.current = false;
            return;
          }
          // Unselected figure children do not receive pointer events, so find the visible tile by its bounds.
          const tile = target.closest<HTMLElement>('[data-lens-item]') ?? (kind === 'kpis'
            ? [...event.currentTarget.querySelectorAll<HTMLElement>('[data-lens-item]')].find((item) => {
                const rect = item.getBoundingClientRect();
                return event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
              })
            : null);
          if (kind === 'kpis' && !tile && !target.closest('button, a, input, select, textarea, [contenteditable="true"], .resize, .resize-bottom')) {
            context.select?.(undefined);
            return;
          }
          const part = selected ? tile ?? target.closest<HTMLElement>('[data-lens-part]') : null;
          const name = part?.dataset.lensItem ?? part?.dataset.lensPart;
          context.select?.(name ? `${block.id}/${name}` : block.id);
        },
        onKeyDown: (event: React.KeyboardEvent) => {
          const figures = Array.isArray(block.kpis) ? (block.kpis as Array<string | { kpi: string }>).map((entry) => (typeof entry === 'string' ? entry : entry.kpi)) : [];
          const at = context.selected?.startsWith(`${block.id}/`) ? figures.indexOf(context.selected.slice(block.id.length + 1)) : -1;
          const step = event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0;
          if (at >= 0 && step) {
            event.preventDefault();
            const neighbour = figures[at + step];
            if (neighbour) editor.apply(step < 0 ? { op: 'move', id: context.selected!, before: `${block.id}/${neighbour}` } : { op: 'move', id: context.selected!, after: `${block.id}/${neighbour}` });
            return;
          }
          if (event.target !== event.currentTarget) return;
          const direction = ARROWS[event.key];
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            context.select?.(block.id);
          } else if (direction) {
            event.preventDefault();
            const operation = moveOperation(context.document.page, block.id, direction);
            if (operation) editor.apply(operation);
          }
        },
      }
    : {};
  const reading =
    !editor && context.follow && kind
      ? {
          onClickCapture: (event: React.MouseEvent) => {
            if (!(event.target as HTMLElement).closest('.ask, a, [role=menu], .MuiPopover-root')) context.follow?.({ id: block.id, title: blockTitle() });
          },
        }
      : {};
  const bar = useRef<HTMLDivElement>(null);
  const [adding, setAdding] = useState(false);
  const inspectAs = (view: BlockPaneView) => () => context.inspect!({ id: block.id, title: blockTitle() }, view);
  const menuItems: Array<BlockMenuItem> = [
    ...(context.inspect ? [{ label: 'About', icon: <InfoOutlinedIcon fontSize="small" />, run: inspectAs('about') }] : []),
    ...(!editor && downloadable
      ? [
          { label: 'Download CSV', icon: <TableChartOutlinedIcon fontSize="small" />, run: () => void download() },
          ...(kind === 'code' ? [] : [{ label: 'Download image', icon: <ImageOutlinedIcon fontSize="small" />, run: () => void image().catch(() => snackbar?.enqueueSnackbar(`${blockTitle()} didn’t save as an image. Try again.`, { variant: 'warning' })) }]),
        ]
      : []),
    ...(context.inspect
      ? [
          { label: 'Show SQL', icon: <CodeOutlinedIcon fontSize="small" />, run: inspectAs('sql') },
          { label: 'View data', icon: <GridOnOutlinedIcon fontSize="small" />, run: inspectAs('data') },
        ]
      : []),
    ...(editor
      ? [
          { label: 'Add block after', icon: <PlaylistAddIcon fontSize="small" />, run: () => setAdding(true), divider: true, disabled: editor.pending },
          {
            label: 'Duplicate',
            icon: <ContentCopyOutlinedIcon fontSize="small" />,
            run: () => {
              const { id, ...copy } = block;
              editor.apply({ op: 'add', block: copy, after: id });
            },
            disabled: editor.pending,
          },
          ...(block.ask ? [{ label: 'Re-ask askRichard', icon: <RefreshIcon fontSize="small" />, run: () => undefined, reask: true }] : []),
          { label: 'Delete', icon: <DeleteOutlineIcon fontSize="small" />, run: () => editor.apply({ op: 'remove', id: block.id }), danger: true, disabled: editor.pending },
        ]
      : [
          {
            label: 'Copy link to this block',
            icon: <LinkIcon fontSize="small" />,
            run: () => {
              const url = new URL(window.location.href);
              url.searchParams.set(LINKED_BLOCK_PARAM, block.id);
              navigator.clipboard.writeText(url.toString()).then(
                () => snackbar?.enqueueSnackbar('Link copied.', { variant: 'success' }),
                () => snackbar?.enqueueSnackbar('The link didn’t copy. Try again.', { variant: 'warning' }),
              );
            },
          },
          ...(context.editBlock ? [{ label: 'Edit this block', icon: <TuneOutlinedIcon fontSize="small" />, run: () => context.editBlock!(block.id), divider: true }] : []),
        ]),
  ];
  const arrived = (planned?.queries ?? (planned?.query ? [planned.query] : [])).map((id) => context.data?.queries[id]);
  if (arrived.length) held.current = arrived.map((entry, index) => entry ?? held.current[index]);
  const envelopes = held.current.filter((entry): entry is Envelope => Boolean(entry));
  const stale = (arrived.some((entry) => !entry) || fades(context.fading, block.id)) && envelopes.length > 0 && !context.failed;
  const failed = envelopes.filter((entry) => entry.status === 'error');
  const { unreached } = controlReach(context, envelopes);
  const ready = envelopes.filter((entry) => entry.status !== 'error');
  const partial = planned?.family === 'figures' && ready.length > 0 && failed.length > 0;
  const envelope = partial ? ready[0] : envelopes[0];
  const failure = planned?.problems?.[0] ?? (partial ? undefined : failed[0]?.error);
  const empty = Boolean(envelope) && !failed.length && !['figures', 'prose', 'none'].includes(planned?.family ?? 'none') && ready.every((entry) => !entry.rows.length);
  const bare = planned ? planned.family === 'figures' : kind === 'kpis' || kind === 'kpi';
  const box = block.frame ?? (bare ? 'tile' : undefined);
  const status = block.status ?? (bare ? 'none' : 'border');
  const className = ['block', bare ? 'bare' : '', box ? `frame-${box}` : '', status === 'none' ? 'status-none' : '', kind === 'callout' ? `callout ${block.tone ?? 'info'}` : '', kind === 'pending' ? 'pending' : '', kind === 'text' && !block.ask ? 'prose' : '', stale ? 'stale' : ''].filter(Boolean).join(' ');
  const View = planned?.view ? VIEWS[planned.view] : undefined;
  const shown = shownFor(block, context.document);

  const body = () => {
    if (failure)
      return (
        <div className="fail" role="alert">
          <b>{failure.code === 'query_timeout' ? 'Too slow to load.' : 'This block could not load.'}</b> {failure.message}
          {context.askAbout ? (
            <button className="fix" onClick={() => context.askAbout!({ id: block.id, title: blockTitle() }, `Fix this card: ${failure.message}`)} type="button">
              Ask askRichard to fix
            </button>
          ) : null}
        </div>
      );
    if (empty) return <p className="empty">Nothing to show for {context.data?.period.current.label ?? 'this period'}.</p>;
    if (kind === 'control') {
      const name = String(block.control).replace(/^\$/, '');
      return <ParamControls change={context.setParam ? (next) => context.setParam!(name, next.params?.[name]) : undefined} document={context.document} labels={context.labels} only={name} state={context.state} />;
    }
    if (kind === 'pending') return <p className="prose">{String(block.pending ?? 'Not built yet')}</p>;
    if (planned?.view === 'prose' && kind) {
      if (picked === '@text' && text) return <InlineText label="Text" multiline onCancel={text.cancel} onCommit={(value) => value && text.commit(kind, value)} value={String(block[kind] ?? '')} />;
      return context.data ? (
        <div data-lens-part="@text">
          <Prose envelopes={envelopes} period={context.data.period} shown={shown} text={String(block[kind] ?? '')} />
        </div>
      ) : null;
    }
    if (!envelope && context.failed) return <div className="fail" role="alert">These numbers didn’t load.</div>;
    if (!envelope) return <div aria-busy className="chart" style={{ height: bare ? 96 : undefined }} />;
    const drawn = View && kind ? View({ block, kind, envelope, envelopes: partial ? ready : envelopes, context, width, shown, picked }) : null;
    return partial ? (
      <>
        {drawn}
        <div className="fail" role="alert">
          <b>Some figures could not load.</b> {failed[0].error?.message}
        </div>
      </>
    ) : (
      drawn
    );
  };

  if (hiddenInView(block, context)) return null;

  const textScale = textScaleOf(block.textSize) ?? textScaleOf(context.document.display.textSize) ?? 1;
  return (
    <SeriesFocusContext.Provider value={context.sharedLegend?.has(block.id) ? rowFocus : ownFocus}>
    <TextScaleContext.Provider value={textScale}>
    <div className={[className, editor ? 'editing' : '', selected ? 'selected' : '', context.asked === block.id ? 'asked' : '', context.landed?.has(block.id) ? 'landed' : '', context.dragging === block.id || (context.dragging && splitBlockId(context.dragging) === block.id) ? 'dragging' : ''].filter(Boolean).join(' ')} data-lens-block={block.id} data-lens-kind={kind} data-size={bare ? undefined : (block.size as string | undefined)} ref={frame} style={{ ...(block.h ? { '--chart-h': `${block.h}px` } : {}), ...(textScale !== 1 ? { '--text-zoom': textScale } : {}) } as React.CSSProperties} {...editing} {...reading}>
      {kind ? (
        <div className="ask ask-bar" ref={bar}>
          {context.askAbout ? (
            <button aria-label={`Ask about ${blockTitle()}`} onClick={() => context.askAbout?.({ id: block.id, title: blockTitle() })} title="Ask about this" type="button">
              <ChatBubbleOutlineIcon fontSize="inherit" />
            </button>
          ) : null}
          <Menu
            block={block}
            context={context}
            icon={<MoreHorizIcon fontSize="inherit" />}
            items={menuItems}
            label={`More for ${blockTitle()}`}
            title="More"
          />
        </div>
      ) : null}
      {editor ? (
        <span onClick={(event) => event.stopPropagation()} onPointerDown={(event) => event.stopPropagation()}>
          <AddBlock after={block.id} anchor={adding ? bar.current : null} data={context.data} document={context.document} editor={editor} onClose={() => setAdding(false)} />
        </span>
      ) : null}
      {selected && editor ? <ResizeHandle block={block} editor={editor} family={planned?.family} frame={frame} /> : null}
      {kind ? (
        <Header
          block={block}
          context={context}
          envelope={envelope}
          kind={kind}
          legend={context.palette && !context.sharedLegend?.has(block.id) ? <Legend font={context.palette.sans} items={blockLegend(block, context, width)} width={width} /> : null}
          planned={planned}
          text={selected ? text : undefined}
        />
      ) : null}
      {unreached.length ? <p className="bn" data-lens-not-filtered>{`Not filtered by ${unreached.join(', ')}`}</p> : null}
      <BlockBoundary data={arrived}>{body()}</BlockBoundary>
      <Reading block={block} envelope={envelope} />
      {picked === '@note' && text ? (
        <InlineText label="Footnote" onCancel={text.cancel} onCommit={(value) => text.commit('note', value)} value={typeof block.note === 'string' ? block.note : ''} />
      ) : typeof block.note === 'string' && block.note ? (
        <p className="bn" data-lens-part="@note">
          {block.note}
        </p>
      ) : null}
    </div>
    </TextScaleContext.Provider>
    </SeriesFocusContext.Provider>
  );
}
